import { useMutation, useQuery } from '@tanstack/react-query';
import api from '../../lib/api';

interface LoginPayload {
  email: string;
  password: string;
}

interface RegisterPayload {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  organizationName: string;
  domain?: string;
}

export function useLogin() {
  return useMutation({
    mutationFn: (payload: LoginPayload) =>
      api.post('/auth/login', payload),
  });
}

export function useRegister() {
  return useMutation({
    mutationFn: (payload: RegisterPayload) =>
      api.post('/auth/register', payload),
  });
}

export function useLogout() {
  return useMutation({
    mutationFn: () => api.post('/auth/logout'),
  });
}

export function useRefreshToken() {
  return useMutation({
    mutationFn: () => api.post('/auth/refresh'),
  });
}

export function useForgotPassword() {
  return useMutation({
    mutationFn: (email: string) =>
      api.post('/auth/forgot-password', { email }),
  });
}

export function useResetPassword() {
  return useMutation({
    mutationFn: (payload: { token: string; password: string }) =>
      api.post('/auth/reset-password', payload),
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: (payload: { currentPassword: string; newPassword: string }) =>
      api.post('/auth/change-password', payload),
  });
}

/** Whether the server offers "Sign in with Google" (public — used on the login page). */
export function useGoogleSignInStatus() {
  return useQuery({
    queryKey: ['auth', 'google-signin'],
    queryFn: async () => {
      const res = (await api.get('/auth/google/status')) as { data?: { enabled?: boolean } };
      return !!res.data?.enabled;
    },
    staleTime: 5 * 60_000,
    retry: false,
  });
}

/** Full-page navigation to the API, which redirects to Google and back. */
export function startGoogleSignIn(): void {
  const base = (import.meta.env.VITE_API_URL || '/api/v1').replace(/\/$/, '');
  window.location.assign(`${base}/auth/google`);
}
